import React from 'react';
import { Button } from '../components/ui/button';
import { Check, Star, Shield, Clock, Sparkles } from 'lucide-react';
import config from '../config';
import { trackEvent } from '../analytics';

export const HolidaySpecial = () => {
  const handleBookSpecial = () => {
    trackEvent('holiday_special_book_click', {
      offer: 'Deep Cleaning Common Area - $180',
      page: '/holiday-special'
    });
    window.open(config.hcp.bookingUrl, '_blank');
  };

  const testimonials = [
    {
      name: 'Mahdy I.',
      rating: 5,
      text: 'Amazing',
      date: 'Dec 4, 2025'
    },
    {
      name: 'Kate O.',
      rating: 5,
      text: 'Great job, everything looks and smells so much better!',
      date: 'Nov 22, 2025'
    },
    {
      name: 'Joe S.',
      rating: 5,
      text: 'Fantastic job, thanks for coming last minute!',
      date: 'Nov 21, 2025'
    }
  ];

  const features = [
    'Complete deep cleaning of all common areas',
    'Kitchen & dining area sanitization',
    'Living room & hallways detailed cleaning',
    'Bathroom deep scrub & disinfection',
    'Floor cleaning & mopping',
    'Dusting of all surfaces & furniture',
    'Trash removal & disposal',
    'Eco-friendly cleaning products'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 via-white to-green-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-red-600 via-green-600 to-red-600 text-white">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Sparkles className="h-5 w-5 text-yellow-300" />
              <span className="text-sm font-semibold">LIMITED TIME OFFER</span>
              <Sparkles className="h-5 w-5 text-yellow-300" />
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              🎄 Holiday Special! 🎁
            </h1>
            
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold mb-8">
              Deep Cleaning Common Area
            </h2>
            
            <div className="flex items-center justify-center gap-4 mb-8">
              <span className="text-2xl sm:text-3xl line-through opacity-70">$250</span>
              <span className="text-5xl sm:text-6xl lg:text-7xl font-bold text-yellow-300">$180</span>
            </div>
            
            <p className="text-lg sm:text-xl mb-8 max-w-2xl mx-auto">
              Get your home sparkling clean for the holidays! Professional deep cleaning service at an unbeatable price.
            </p>
            
            <Button 
              onClick={handleBookSpecial}
              size="lg"
              className="bg-yellow-400 hover:bg-yellow-300 text-gray-900 px-4 sm:px-8 py-4 sm:py-6 text-base sm:text-xl font-bold rounded-full shadow-2xl hover:shadow-yellow-400/50 transition-all duration-300 hover:scale-105"
            >
              <span className="hidden sm:inline">🎁 BOOK NOW - SAVE $70!</span>
              <span className="sm:hidden">🎁 BOOK NOW</span>
            </Button>
          </div>
        </div>
      </div>

      {/* What's Included Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            What's Included
          </h2>
          <p className="text-lg text-gray-600">
            Everything you need for a spotless common area
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm border border-green-100 hover:shadow-md transition-shadow"
            >
              <div className="flex-shrink-0 w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                <Check className="h-4 w-4 text-white" />
              </div>
              <span className="text-gray-700 font-medium">{feature}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Lead Capture Section */}
      <div className="bg-gradient-to-r from-green-600 to-red-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Transform Your Space?
          </h2>
          <p className="text-xl mb-8">
            Book now and enjoy a pristine home for the holidays!
          </p>
          <Button 
            onClick={handleBookSpecial}
            size="lg"
            className="bg-white hover:bg-gray-100 text-green-700 px-4 sm:px-8 py-4 sm:py-6 text-base sm:text-xl font-bold rounded-full shadow-2xl hover:shadow-white/30 transition-all duration-300 hover:scale-105"
          >
            <span className="hidden sm:inline">📞 BOOK YOUR APPOINTMENT NOW</span>
            <span className="sm:hidden">📞 BOOK NOW</span>
          </Button>
          <p className="mt-6 text-sm opacity-90">
            Limited slots available • Offer ends December 31, 2025
          </p>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            What Our Customers Say
          </h2>
          <p className="text-lg text-gray-600">
            Real reviews from satisfied clients
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow border border-gray-100"
            >
              <div className="flex items-center gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
              <div className="flex items-center justify-between">
                <p className="font-semibold text-gray-900">{testimonial.name}</p>
                <p className="text-sm text-gray-500">{testimonial.date}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Guarantee Section */}
      <div className="bg-blue-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
            <Shield className="h-16 w-16 text-blue-600 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              100% Satisfaction Guarantee
            </h2>
            <p className="text-lg text-gray-700 mb-6">
              If you're not completely satisfied with our service, we'll come back and make it right - absolutely FREE!
            </p>
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Check className="h-8 w-8 text-green-600" />
                </div>
                <p className="font-semibold text-gray-900">Licensed & Insured</p>
              </div>
              <div className="text-center">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Star className="h-8 w-8 text-green-600" />
                </div>
                <p className="font-semibold text-gray-900">5-Star Rated</p>
              </div>
              <div className="text-center">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Clock className="h-8 w-8 text-green-600" />
                </div>
                <p className="font-semibold text-gray-900">On-Time Service</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Urgency Section */}
      <div className="bg-gradient-to-r from-red-600 to-green-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Clock className="h-16 w-16 mx-auto mb-6 animate-pulse" />
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            ⏰ Don't Miss Out!
          </h2>
          <p className="text-xl mb-6">
            This exclusive holiday offer is only available for a limited time.
          </p>
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 mb-8 max-w-2xl mx-auto">
            <p className="text-2xl font-bold mb-2">🎁 BONUS OFFER 🎁</p>
            <p className="text-lg">
              Book before December 25th and receive a FREE oven inside deep cleaning!
            </p>
          </div>
          <Button 
            onClick={handleBookSpecial}
            size="lg"
            className="bg-yellow-400 hover:bg-yellow-300 text-gray-900 px-4 sm:px-8 py-4 sm:py-6 text-base sm:text-xl font-bold rounded-full shadow-2xl hover:shadow-yellow-400/50 transition-all duration-300 hover:scale-105"
          >
            <span className="hidden sm:inline">🎄 CLAIM YOUR HOLIDAY DEAL NOW!</span>
            <span className="sm:hidden">🎄 CLAIM DEAL</span>
          </Button>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg mb-2">
            Questions? Call us at{' '}
            <a 
              href={`tel:${config.contact.phone}`} 
              className="text-green-400 hover:text-green-300 font-semibold"
            >
              {config.contact.phoneDisplay}
            </a>
          </p>
          <p className="text-gray-400 text-sm">
            Serving {config.contact.serviceAreas.join(' • ')}
          </p>
        </div>
      </div>
    </div>
  );
};